import React, { useState } from "react";
import { useNavigate, Navigate } from "react-router-dom";
import { Lock, Mail, Loader2, ArrowRight, Smartphone } from "lucide-react";
import { useAuth } from "../context/AuthContext";
import { JenburktBrand, CLAIMark } from "../components/Brand";
import { formatError } from "../lib/api";

export default function Login() {
  const { user, login, mobileLogin } = useAuth();
  const nav = useNavigate();
  const [mode, setMode] = useState("otp"); // otp (field, now direct mobile) | email (HQ)
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [mobile, setMobile] = useState("");
  const [err, setErr] = useState("");
  const [loading, setLoading] = useState(false);

  // Hide demo credentials on production custom domain; show on preview hosts.
  const showDemoCreds = typeof window !== "undefined" && /(preview\.emergentagent\.com|localhost)/i.test(window.location.host);

  if (user) return <Navigate to="/dashboard" replace />;

  async function submit(e) {
    e.preventDefault();
    setErr(""); setLoading(true);
    try {
      if (mode === "email") {
        await login(email, password);
        nav("/");
      } else {
        await mobileLogin(mobile);
        nav("/");
      }
    } catch (e) {
      setErr(formatError(e));
    } finally {
      setLoading(false);
    }
  }

  return (
    <div className="min-h-screen flex">
      {/* Left brand panel */}
      <div className="hidden lg:flex w-1/2 relative bg-brand-navy text-white overflow-hidden">
        <img
          src="https://images.unsplash.com/photo-1773637780053-6ff7eaec404c?crop=entropy&cs=srgb&fm=jpg&w=1600&q=85"
          alt="abstract"
          className="absolute inset-0 w-full h-full object-cover opacity-25"
        />
        <div className="absolute inset-0 bg-gradient-to-br from-brand-navy via-brand-navy/95 to-teal-900/60" />
        <div className="relative z-10 flex flex-col justify-between p-12 w-full">
          <div className="flex items-center gap-3">
            <div className="h-14 w-16 rounded-md bg-white p-1.5 flex items-center justify-center">
              <img src="/brand/jenburkt.png" alt="Jenburkt" className="h-full w-full object-contain" />
            </div>
            <div>
              <div className="text-xl font-semibold tracking-tight">Jenburkt <span className="opacity-70 font-normal">Pharmaceuticals</span></div>
              <div className="small-caps text-teal-300 mt-1">Delivering Excellence in Life Sciences</div>
            </div>
          </div>

          <div className="space-y-6">
            <div>
              <div className="small-caps text-teal-300">CBC Program</div>
              <h1 className="serif-display text-5xl xl:text-6xl font-medium leading-[1.05] mt-2">
                Champion <span className="italic opacity-80">Behind the</span><br/> Champions.
              </h1>
              <p className="text-slate-300 mt-4 max-w-md text-[15px] leading-relaxed">
                An AI-powered loyalty operating system for India&apos;s physiotherapy community — built around the <span className="text-teal-300 font-medium">ZIXA Strong</span> brand promise.
              </p>
            </div>
          </div>

          <CLAIMark tone="light" />
        </div>
      </div>

      {/* Right form */}
      <div className="flex-1 flex flex-col bg-white">
        <div className="flex-1 flex items-center justify-center p-8">
          <div className="w-full max-w-md">
            <div className="lg:hidden mb-8"><JenburktBrand /></div>
            <div className="small-caps text-teal-700">Secure Sign In</div>
            <h2 className="serif-display text-4xl mt-1 text-slate-900 tracking-tight">Welcome back.</h2>
            <p className="text-slate-500 mt-2 text-[14px]">Continue to the CBC operations workspace.</p>

            <form onSubmit={submit} className="mt-8 space-y-4" data-testid="login-form">
              <div className="flex items-center gap-2 mb-2 border-b border-slate-100">
                <button type="button" onClick={() => { setMode("otp"); setErr(""); }} data-testid="mode-otp"
                  className={`px-3 py-2 text-[12px] font-medium border-b-2 ${mode === "otp" ? "border-brand-navy text-brand-navy" : "border-transparent text-slate-500 hover:text-slate-800"}`}>Field — Mobile</button>
                <button type="button" onClick={() => { setMode("email"); setErr(""); }} data-testid="mode-email"
                  className={`px-3 py-2 text-[12px] font-medium border-b-2 ${mode === "email" ? "border-brand-navy text-brand-navy" : "border-transparent text-slate-500 hover:text-slate-800"}`}>HQ — Email</button>
              </div>

              {mode === "email" && (
                <>
                  <div>
                    <label className="small-caps text-slate-500">Email</label>
                    <div className="relative mt-1">
                      <Mail size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
                      <input type="email" value={email} onChange={(e) => setEmail(e.target.value)} className="w-full pl-9" required data-testid="login-email" />
                    </div>
                  </div>
                  <div>
                    <label className="small-caps text-slate-500">Password</label>
                    <div className="relative mt-1">
                      <Lock size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
                      <input type="password" value={password} onChange={(e) => setPassword(e.target.value)} className="w-full pl-9" required data-testid="login-password" />
                    </div>
                  </div>
                </>
              )}

              {mode === "otp" && (
                <div>
                  <label className="small-caps text-slate-500">Mobile number</label>
                  <div className="relative mt-1">
                    <Smartphone size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
                    <input
                      type="tel"
                      value={mobile}
                      onChange={(e) => setMobile(e.target.value.replace(/[^0-9]/g, "").slice(0, 10))}
                      className="w-full pl-9 font-mono tracking-wider"
                      placeholder="10-digit registered mobile"
                      required
                      maxLength={10}
                      data-testid="login-mobile"
                    />
                  </div>
                  <div className="text-[10px] text-slate-400 mt-1">Only registered Jenburkt L2 (DSE) and L5 (Zonal Manager) mobiles can sign in.</div>
                </div>
              )}

              {err && <div className="tag tag-rose w-full justify-start" data-testid="login-error">{err}</div>}

              <button type="submit" disabled={loading} className="btn-primary w-full flex items-center justify-center gap-2 !py-2.5 group" data-testid="login-submit">
                {loading ? <Loader2 size={16} className="animate-spin" /> : (
                  <>Sign in <ArrowRight size={14} className="transition-transform group-hover:translate-x-0.5" /></>
                )}
              </button>

              <div className="text-[12px] text-slate-500 mt-3 border-t border-slate-100 pt-4">
                {showDemoCreds ? (
                  <>
                    <div className="small-caps mb-2 text-slate-400">Demo Credentials</div>
                    <div className="space-y-1 font-mono text-[11.5px] text-slate-600">
                      <div>HQ — superadmin@channelloyalty.ai · ChannelLoyalty@2026</div>
                      <div>Field (L2) — 8249106584 · 8652260451 · 9921872557 · …</div>
                      <div>Field (L5) — 8530377697 (Sachin Chouhan) · 9148425037 (Basavaraja MT)</div>
                    </div>
                  </>
                ) : (
                  <div className="small-caps text-slate-400">Need access? Contact your CBC programme administrator.</div>
                )}
              </div>
            </form>
          </div>
        </div>
        <div className="border-t border-slate-100 px-8 py-4 flex items-center justify-between text-[11px] text-slate-500">
          <div>© {new Date().getFullYear()} Jenburkt Pharmaceuticals Ltd.</div>
          <CLAIMark />
        </div>
      </div>
    </div>
  );
}
